import Keycloak from "keycloak-js";
import { httpClient } from "./HttpClient";

const authEnabled = process.env.REACT_APP_AUTH_ENABLED === "true";
const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL;
const keycloakRealm = process.env.REACT_APP_KEYCLOAK_REALM;
const keycloakClientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;

const ADMIN_ROLE = process.env.REACT_APP_ADMIN_ROLE;
const OPERATOR_ROLE = process.env.REACT_APP_OPERATOR_ROLE;
const QA_ROLE = process.env.REACT_APP_QA_ROLE;

let kc = null;
let keycloakUserId = null;
let userEmail = localStorage.getItem("userEmail");
let userRoles = [];

if (authEnabled) {
  const initOptions = {
    url: keycloakUrl,
    realm: keycloakRealm,
    clientId: keycloakClientId,
  };
  kc = new Keycloak(initOptions);
} else {
  console.log("Authentication disabled for testing");
}

// Function to initialize Keycloak and handle authentication setup
const initializeKeycloak = (onInitialized) => {
  return new Promise((resolve, reject) => {
    if (authEnabled && kc) {
      kc.init({
        onLoad: "login-required",
        checkLoginIframe: false,
        pkceMethod: "S256",
      })
        .then((authenticated) => {
          if (authenticated) {
            // Store token and user info
            localStorage.setItem("accessToken", kc.token);
            const tokenParsed = kc.tokenParsed;
            keycloakUserId = tokenParsed?.sub || null;
            userEmail = tokenParsed?.email || null;
            userRoles = tokenParsed?.realm_access?.roles || [];
            if (keycloakUserId) localStorage.setItem("keycloakUserId", keycloakUserId);
            if (userEmail) localStorage.setItem("userEmail", userEmail);
            if (tokenParsed?.tenantId) localStorage.setItem("tenantId", tokenParsed.tenantId);

            // Set Authorization header for HTTP client
            httpClient.defaults.headers.common["Authorization"] = `Bearer ${kc.token}`;

            // Handle token expiration
            kc.onTokenExpired = () => {
              console.log("Token expired");
              kc.updateToken(30).catch(() => {
                kc.logout();
              });
            };
          }
          onInitialized(); 
          resolve(authenticated); 
        })
        .catch((error) => {
          console.error("Keycloak initialization failed:", error);
          onInitialized(); 
          reject(error); 
        });
    } else {
      onInitialized();
      resolve(true);
    }
  });
};

const hasRole = (role) => Boolean(role) && userRoles.includes(role);

// With auth disabled (local testing only) there are no roles, so everything stays visible as before.
const isAdmin = () => !authEnabled || hasRole(ADMIN_ROLE);
const isOperator = () => hasRole(OPERATOR_ROLE);
const isQA = () => hasRole(QA_ROLE);

// OPERATOR and QA share the same restricted scope (OTA artifacts/campaigns, hubs, remote terminal)
// unless the user is also an ADMIN. They stay separate roles so the UI and the backend can tell them apart.
const isRestrictedRoleOnly = () => (isOperator() || isQA()) && !hasRole(ADMIN_ROLE);

// Kept for existing callers; true for OPERATOR-only users (not QA).
const isOperatorOnly = () => isOperator() && !hasRole(ADMIN_ROLE);

const getUserRoleLabel = () => {
  if (hasRole(ADMIN_ROLE)) return ADMIN_ROLE;
  const restrictedRoles = [OPERATOR_ROLE, QA_ROLE].filter(hasRole);
  if (restrictedRoles.length > 0) return restrictedRoles.join(", ");
  return userRoles.join(", ");
};

export {
  kc,
  authEnabled,
  keycloakUserId,
  userEmail,
  initializeKeycloak,
  isAdmin,
  isOperator,
  isQA,
  isRestrictedRoleOnly,
  isOperatorOnly,
  getUserRoleLabel,
};